/* typesense-search.js — Algolia-class instant search via Typesense.
 * The heavy libs (InstantSearch.js + adapter) are vendored theme assets loaded
 * ONLY on first search open, so they never touch the first-load budget. */
(() => {
  'use strict';
  const cfg = window.TYPESENSE;
  if (!cfg || !cfg.apiKey) return;
  let started = false;

  const money = (n) => { try { return Number(n).toLocaleString('en-AU', { style: 'currency', currency: cfg.currency || 'AUD' }); } catch { return '$' + n; } };

  async function boot(mountInto) {
    if (started) return; started = true;
    const loadScript = (src) => new Promise((res, rej) => {
      const el = document.createElement('script');
      el.src = src; el.onload = res; el.onerror = rej;
      document.head.appendChild(el);
    });
    // vendored official dist builds (theme assets) — no third-party CDN at runtime
    await loadScript(cfg.libs.is);
    await loadScript(cfg.libs.adapter);
    const instantsearch = window.instantsearch;
    const { searchBox, hits, refinementList, rangeInput, sortBy, stats, pagination, currentRefinements, clearRefinements, configure } = instantsearch.widgets;
    const TypesenseInstantSearchAdapter = window.TypesenseInstantSearchAdapter;

    const adapter = new TypesenseInstantSearchAdapter({
      server: {
        apiKey: cfg.apiKey,
        nodes: [{ host: cfg.host, port: cfg.port || '443', protocol: cfg.protocol || 'https' }],
        cacheSearchResultsForSeconds: 120,
      },
      additionalSearchParameters: {
        query_by: 'title,vendor,skus,product_type,tags',
        query_by_weights: '6,4,5,3,2',
        sort_by: '_text_match:desc',
        highlight_full_fields: 'title',
        typo_tokens_threshold: 1,
      },
    });

    const search = instantsearch({
      searchClient: adapter.searchClient,
      indexName: cfg.collection || 'products',
      future: { preserveSharedStateOnUnmount: true },
    });

    const hitTemplate = (hit, { html, components }) => html`
      <a class="ts-hit" href="/products/${hit.handle}">
        <span class="ts-hit__media media">${hit.image ? html`<img loading="lazy" src="${hit.image}" alt="" />` : ''}</span>
        <span class="ts-hit__meta">
          <span class="ts-hit__vendor">${(hit.vendor && !(window.STBrand ? window.STBrand.hidden(hit) : (Array.isArray(window.ST_BRAND_HIDDEN_TYPES) && window.ST_BRAND_HIDDEN_TYPES.includes(hit.product_type)))) ? hit.vendor : ''}</span>
          <span class="ts-hit__title">${components.Highlight({ hit, attribute: 'title' })}</span>
          <span class="ts-hit__price">${(window.STBadges && window.STBadges.isSaving && window.STBadges.isSaving(hit.price, hit.compare_at_price)) ? html`<s>${money(hit.compare_at_price)}</s> ` : ''}${money(hit.price)}</span>
        </span>
      </a>`;

    mountInto.innerHTML = `
      <div class="ts">
        <div class="ts__facets">
          <div data-w="clear"></div>
          <div class="ts__facet"><h4>Brand</h4><div data-w="vendor"></div></div>
          <div class="ts__facet"><h4>Type</h4><div data-w="type"></div></div>
          <div class="ts__facet"><h4>Finish</h4><div data-w="finish"></div></div>
          <div class="ts__facet"><h4>Price</h4><div data-w="price"></div></div>
          <div class="ts__facet"><label><span data-w="instock"></span></label></div>
        </div>
        <div class="ts__main">
          <div class="ts__bar"><div data-w="stats"></div><div data-w="sort"></div></div>
          <div data-w="current"></div>
          <div class="ts__hits" data-w="hits"></div>
          <div data-w="pagination"></div>
        </div>
      </div>`;
    const W = (n) => mountInto.querySelector(`[data-w="${n}"]`);

    search.addWidgets([
      configure({ hitsPerPage: 24 }),
      searchBox({ container: document.createElement('div'), /* we drive query from the overlay input */ }),
      stats({ container: W('stats') }),
      sortBy({ container: W('sort'), items: [
        { label: 'Relevance', value: cfg.collection },
        { label: 'Price ↑', value: `${cfg.collection}/sort/price:asc` },
        { label: 'Price ↓', value: `${cfg.collection}/sort/price:desc` },
        { label: 'Newest', value: `${cfg.collection}/sort/created_at:desc` },
      ]}),
      refinementList({ container: W('vendor'), attribute: 'vendor', searchable: true, limit: 8, showMore: true }),
      refinementList({ container: W('type'), attribute: 'product_type', limit: 8, showMore: true }),
      refinementList({ container: W('finish'), attribute: 'colours', limit: 12, showMore: true }),
      refinementList({ container: W('instock'), attribute: 'available', transformItems: (i) => i.filter((x) => x.value === 'true').map((x) => ({ ...x, label: 'In stock only' })) }),
      rangeInput({ container: W('price'), attribute: 'price' }),
      currentRefinements({ container: W('current') }),
      clearRefinements({ container: W('clear'), templates: { resetLabel: 'Clear all' } }),
      pagination({ container: W('pagination') }),
      hits({ container: W('hits'), templates: { item: hitTemplate, empty: 'No matches — try a brand, type, or SKU.' } }),
    ]);
    search.start();

    // drive the query from our existing overlay input
    const input = document.querySelector('[data-search-input]');
    if (input) input.addEventListener('input', () => search.helper.setQuery(input.value).search());
    return search;
  }

  // lazy init on first search open
  const results = document.querySelector('[data-search-results]');
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-search-toggle]') && results) boot(results);
  }, { capture: true });
  // full-page /search instance
  const page = document.getElementById('ts-search');
  if (page) (window.requestIdleCallback || setTimeout)(() => boot(page));
})();
